import React, {useEffect, useState} from 'react';
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';
import classNames from 'classnames';
import ComboBoxDateRange from 'components/Basic/ComboBox/ComboBoxDateRange';
import {DateRange} from 'components/Basic/DateRangeV2/types';
import dayjs from 'dayjs';
import {selectRevenueChartData} from 'features/Provider/providerSelectors';
import {getProviderRevenueChartData} from 'features/Provider/providerSlice';
import {selectUserProfile} from 'features/User';
import {Line} from 'react-chartjs-2';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
);

interface ChartOptions {
  responsive: boolean;
  maintainAspectRatio: boolean;
  plugins: {
    legend: {
      display: boolean;
    };
    title: {
      display: boolean;
      text: string;
    };
  };
  scales: {
    x: {
      grid: {
        display: boolean;
        drawBorder: boolean;
        lineWidth: (ctx: {index: number}) => number;
      };
      ticks: {
        colors: string;
        callback: (
          value: any,
          index: number,
          ticks: any[],
        ) => string | undefined;
      };
    };
    y: {
      grid: {
        display: boolean;
      };
      ticks: {
        colors: string;
        callback: (
          value: any,
          index: number,
          ticks: any[],
        ) => string | undefined;
      };
    };
  };
}

export const RevenueChart: React.FC = () => {
  const user = useSelector(selectUserProfile);
  const dispatch = useDispatch();
  const revenueData = useSelector(selectRevenueChartData);

  const [filters, setFilters] = useState<{
    dateRange: {
      from: Date | null;
      to: Date | null;
    };
  }>({
    dateRange: {from: null, to: null},
  });

  const handleDayClick = (range: DateRange) => {
    setFilters({
      dateRange: {
        from: range.startDate,
        to: range.endDate,
      },
    });
  };

  const formatDate = (date: Date | null) => {
    if (!date) return undefined;
    return dayjs(date).format('YYYY-MM-DD');
  };

  const {t} = useTranslation();

  useEffect(() => {
    dispatch(
      getProviderRevenueChartData({
        providerId: user?._id ?? '',
        startDate: formatDate(filters.dateRange.from),
        endDate: formatDate(filters.dateRange.to),
      }),
    );
  }, [filters.dateRange, dispatch, user?._id]);
  const getAllSessionRevenues = () => {
    const billable = revenueData.data.billable.reduce(
      (acc, curr) => acc + curr,
      0,
    );
    const paid = revenueData.data.paid.reduce((acc, curr) => acc + curr, 0);
    const insuranceAccepted = revenueData.data.insuranceAccepted.reduce(
      (acc, curr) => acc + curr,
      0,
    );
    const insuranceDenied = revenueData.data.insuranceDenied.reduce(
      (acc, curr) => acc + curr,
      0,
    );
    return [
      {type: 'billable', amount: billable, label: 'All Billable Sessions'},
      {type: 'paid', amount: paid, label: 'All paid'},
      {
        type: 'insurance_accepted',
        amount: insuranceAccepted,
        label: 'Insurance Accepted',
      },
      {
        type: 'insurance_denied',
        amount: insuranceDenied,
        label: 'Insurance Denied',
      },
    ];
  };
  const total = revenueData.data.paid.reduce((acc, curr) => acc + curr, 0);

  const data = {
    labels: revenueData.labels,
    datasets: [
      {
        label: 'All Billable Sessions',
        data: revenueData.data.billable,
        fill: false,
        borderColor: 'rgba(249, 168, 212)', // pink-300
        tension: 0.4,
      },
      {
        label: 'All paid',
        data: revenueData.data.paid,
        fill: false,
        borderColor: 'rgba(252, 211, 77)', // yellow-300
        tension: 0.4,
      },
      {
        label: 'Insurance Accepted',
        data: revenueData.data.insuranceAccepted,
        fill: false,
        borderColor: 'rgba(37, 99, 235)', // blue-600
        tension: 0.4,
      },
      {
        label: 'Insurance Denied',
        data: revenueData.data.insuranceDenied,
        fill: false,
        borderColor: 'rgba(156, 163, 175)', // gray-400
        tension: 0.4,
      },
    ],
  };

  const options: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
        text: 'Revenue Over Time',
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
          drawBorder: false,
          lineWidth: function (ctx) {
            return ctx.index === 0 ? 0 : 1; // Hide first grid line
          },
        },
        ticks: {
          colors: 'white',
          callback: function (
            _value: any,
            index: number,
            ticks: any[],
          ): string | undefined {
            const label = data.labels?.[index];
            // Only show first and last tick on x-axis
            if (index === 0 || index === ticks.length - 1) {
              return label; // Just return value directly
            }
            return '';
          },
        },
      },
      y: {
        grid: {
          display: false,
        },
        ticks: {
          colors: 'white',
          callback: function (value: any, index: number, ticks: any[]) {
            // Only show first and last tick on y-axis
            if (index === 0 || index === ticks.length - 1) {
              return value;
            }
            return '';
          },
        },
      },
    },
  };

  return (
    <div className="bg-white  border border-gray-300 shadow-sm rounded-2xl p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start gap-10 sm:items-center mb-6">
        <div>
          <h2 className="text-black font-semibold text-[36px]">
            ${total.toFixed(2)}
          </h2>
          <p className="text-black text-sm font-semibold">Total Revenue</p>
        </div>
        <div className="w-full sm:w-64">
          <ComboBoxDateRange
            label={t('singleSession.date_range')}
            initialRange={{
              startDate: filters.dateRange.from || null,
              endDate: filters.dateRange.to || null,
            }}
            initialPreset="custom"
            onChange={handleDayClick}
          />
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-10 mb-6">
        {getAllSessionRevenues().map(session => (
          <div key={session.type} className="flex flex-col gap-2 col-span-1">
            <div>
              <h3 className="text-black font-semibold text-[18px]">
                ${session.amount.toFixed(2)}
              </h3>
            </div>
            <div className="flex items-center gap-2">
              <div>
                <p className="text-black font-medium">{session.label}</p>
              </div>
              <div
                className={classNames('w-4 h-4 rounded-full', {
                  'bg-pink-300': session.type === 'billable',
                  'bg-yellow-300': session.type === 'paid',
                  'bg-blue-600': session.type === 'insurance_accepted',
                  'bg-gray-400': session.type === 'insurance_denied',
                })}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Placeholder for the chart component */}
      <div className="h-64  w-full">
        <Line options={options} data={data} />
      </div>
    </div>
  );
};
